import { useEffect, useMemo, useState } from 'react';

import type {
  GroupByField,
  TransactionsSummaryFilter,
} from '~domains/dashboard/entities/summary';

import type { DataTableSorting } from '~shared/ui/data-table';
import { useColumnVisibility } from '~shared/ui/data-table';

import type { VisibilityState } from '@tanstack/react-table';

import { useSummaryTableData } from './summary-table-queries';

import { ALL_COLUMN_IDS, ColumnId, getColumnOptions } from '../lib/columns';
import { sortToParams } from '../lib/search-params-converter';

const DEFAULT_PER_PAGE = 10;
const DEFAULT_GROUP_BY: GroupByField = 'by-merchant';
const DEFAULT_SORTING: DataTableSorting = {
  id: ColumnId.SALES_AMOUNT,
  direction: 'desc',
};

export function useSummaryTable(filters?: TransactionsSummaryFilter) {
  const [groupBy, setGroupBy] = useState<GroupByField>(DEFAULT_GROUP_BY);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [sorting, setSorting] = useState<DataTableSorting>();
  const { visibleColumns, setVisibleColumns } = useColumnVisibility(
    ALL_COLUMN_IDS,
    {
      storageKey: 'summary-table:column-visibility',
    }
  );

  useEffect(() => {
    setPage(0);
  }, [filters]);

  const { data, isLoading } = useSummaryTableData(
    filters
      ? {
          groupBy,
          searchParams: {
            sort: sorting && sortToParams(sorting),
            page,
            perPage,
            filter: filters,
          },
        }
      : undefined
  );

  const columnOptions = useMemo(() => getColumnOptions(groupBy), [groupBy]);

  const effectiveVisibleColumns = useMemo(() => {
    const availableIds: string[] = columnOptions.map((opt) => opt.id);
    const intersected = visibleColumns.filter((id) =>
      availableIds.includes(id)
    );
    return intersected.length > 0 ? intersected : availableIds;
  }, [visibleColumns, columnOptions]);

  const columnVisibility = useMemo<VisibilityState>(
    () =>
      Object.fromEntries(
        ALL_COLUMN_IDS.map((id) => [id, effectiveVisibleColumns.includes(id)])
      ),
    [effectiveVisibleColumns]
  );

  const handleGroupByChange = (value: GroupByField) => {
    setGroupBy(value);
    setPage(0);
  };

  const handleSortingChange = (next: DataTableSorting) => {
    setSorting(next);
    setPage(0);
  };

  return {
    data,
    isLoading,
    hasRows: !!data?.items?.length,
    groupBy,
    onGroupByChange: handleGroupByChange,
    sorting,
    sortValue: sorting ?? DEFAULT_SORTING,
    onSortingChange: handleSortingChange,
    visibleColumns: effectiveVisibleColumns,
    setVisibleColumns,
    columnVisibility,
    columnOptions,
    page,
    perPage,
    onPageChange: setPage,
    onPageSizeChange: (size: number) => {
      setPerPage(size);
      setPage(0);
    },
  };
}

export type UseSummaryTableReturn = ReturnType<typeof useSummaryTable>;
